// =============================================================================
// US STATES AND CITIES — a drop-in replacement for the `country-state-city`
// package, carrying only the country this app actually asks for.
//
// 🔴 WHY THIS FILE EXISTS, AND IT IS NOT TIDINESS.
//
//    `country-state-city` inlines its data as JSON, and webpack cannot tree-shake
//    data. Importing `{ State, City }` pulled the WHOLE WORLD into main.js:
//        lib/assets/city.json    8,064,491 bytes  (148,038 cities)
//        lib/assets/state.json     554,703 bytes  (  4,963 states)
//    — 8.7 MB, 81% of a 10,712,997-byte bundle, to serve six call sites that are
//    every one of them hardcoded to 'US'.
//
//    That mattered for a reason nobody would ever see in a log: CloudFront only
//    gzips objects UNDER 10,000,000 bytes. The bundle was 713 KB over that
//    ceiling, so it was served RAW — 10.7 MB on every first visit, with no error,
//    no warning, and a `content-encoding` header nobody reads. Crossing back under
//    the line is worth ~8.7 MB of transfer per new visitor.
//
//    US only: 66 states, 19,821 cities, 264 KB.
//
// 🔒 THE SHAPE IS DELIBERATELY THE LIBRARY'S, not a nicer one. The three signup
//    funnels changed exactly one line each (their import), so this cannot alter
//    what they render. Verified module-against-library for all 66 states and all
//    19,821 cities before the swap — identical order, identical names.
//
// ⚠️ `latitude` / `longitude` are the ONLY fields dropped. Nothing in src/ reads
//    them off a state or a city (the two lat/lng in CreateEventOpportunity.js come
//    from react-google-autocomplete). Adding them back means regenerating with
//    those fields, not editing this file by hand.
//
// ⚠️ THE PACKAGE IS STILL IN package.json AND STILL IN node_modules. It is simply
//    no longer imported, so it contributes zero bytes. **Do not `import` it again**
//    — one import anywhere puts 8.7 MB back and silently re-crosses the
//    compression ceiling. Use this file. If a future screen genuinely needs a
//    second country, regenerate this data with that country included rather than
//    reaching for the package.
//
// REGENERATE (from a-client/, with country-state-city still installed):
//   node -e '
//     const {State,City}=require("country-state-city"), fs=require("fs");
//     const st=State.getStatesOfCountry("US");
//     const out={states:st.map(s=>[s.name,s.isoCode]),cities:{}};
//     for(const s of st) out.cities[s.isoCode]=City.getCitiesOfState("US",s.isoCode).map(c=>c.name);
//     fs.writeFileSync("src/usLocations.json",JSON.stringify(out)+"\n");
//   '
// The library sorts by name with a plain `<`/`>` comparator, so the order in the
// generated file is already the order the dropdowns had. Nothing re-sorts here.
// =============================================================================

import data from './usLocations.json';

const COUNTRY_CODE = 'US';

// Returned for any country this dataset does not carry. A shared frozen array, so
// a caller that stores it in React state and maps over it behaves exactly as it
// did — nothing in the three funnels mutates either list.
const EMPTY = Object.freeze([]);

// Rehydrated once, on first use, and only for the state actually asked for. The
// compact [name, isoCode] / [name] arrays in the JSON exist to keep the bundle
// small; every consumer still sees the objects the library returned.
let statesCache = null;
const citiesCache = {};

export const State = {
    /** The library's `State.getStatesOfCountry`. Non-US returns []. */
    getStatesOfCountry(countryCode) {
        if (countryCode !== COUNTRY_CODE) return EMPTY;
        if (!statesCache) {
            statesCache = data.states.map((entry) => ({
                name: entry[0],
                isoCode: entry[1],
                countryCode: COUNTRY_CODE,
            }));
        }
        return statesCache;
    },
};

export const City = {
    /** The library's `City.getCitiesOfState`. A missing state code returns []. */
    getCitiesOfState(countryCode, stateCode) {
        if (countryCode !== COUNTRY_CODE || !stateCode) return EMPTY;
        if (!citiesCache[stateCode]) {
            const names = data.cities[stateCode];
            if (!names) return EMPTY;
            citiesCache[stateCode] = names.map((name) => ({
                name: name,
                countryCode: COUNTRY_CODE,
                stateCode: stateCode,
            }));
        }
        return citiesCache[stateCode];
    },
};
